import { useState, useEffect } from 'react';
import {
    UPGRADE_SOURCE_QUERY_KEY,
    WAS_UPGRADED_QUERY_KEY,
    SELLER_ID_QUERY_KEY,
    SELLER_NAME_QUERY_KEY,
} from '@fiverr-private/upsell';
import { ComplianceType } from '../components/ComplianceModal/types';

export interface UpgradeRedirectInfo {
    upgradeTriggered: boolean;
    upgradeSource?: ComplianceType;
    sellerId?: number;
    sellerUsername?: string;
}

export interface UseRedirectFromUpgradePaymentOptions {
    onSellerIdChange: (sellerId: number) => void;
    onSellerNameChange: (sellerName: string) => void;
    onSourceChange: (source: ComplianceType) => void;
    onQueryTriggered: () => void;
}

const useRedirectFromUpgradePayment = ({
    onSellerIdChange,
    onSellerNameChange,
    onSourceChange,
    onQueryTriggered,
}: UseRedirectFromUpgradePaymentOptions): UpgradeRedirectInfo => {
    const [upgradeInfo, setUpgradeInfo] = useState<UpgradeRedirectInfo>({
        upgradeTriggered: false,
    });
    const [hasInitializedParams, setHasInitializedParams] = useState(false);

    useEffect(() => {
        const url = new URL(window?.location.href);
        const searchParams = url.searchParams;
        const sellerUsername = searchParams.get(SELLER_NAME_QUERY_KEY);
        const sellerId = searchParams.get(SELLER_ID_QUERY_KEY);
        const upgradePlan = searchParams.get(WAS_UPGRADED_QUERY_KEY);
        const upgradeSource = searchParams.get(UPGRADE_SOURCE_QUERY_KEY);
        if (upgradePlan) {
            setUpgradeInfo({
                upgradeTriggered: true,
                upgradeSource: (upgradeSource as ComplianceType) ?? undefined,
                sellerId: Number(sellerId) ?? -1,
                sellerUsername: String(sellerUsername),
            });

            onQueryTriggered();
            onSellerNameChange(sellerUsername || '');
            onSellerIdChange(Number(sellerId));
            onSourceChange(upgradeSource as ComplianceType);
            setHasInitializedParams(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (hasInitializedParams) {
            const url = new URL(window?.location.href);
            const searchParams = url.searchParams;
            searchParams.delete(WAS_UPGRADED_QUERY_KEY);
            searchParams.delete(UPGRADE_SOURCE_QUERY_KEY);
            searchParams.delete(SELLER_ID_QUERY_KEY);
            searchParams.delete(SELLER_NAME_QUERY_KEY);
            const newUrl = `${url.pathname}?${searchParams.toString()}`.replace(/\?$/, '');
            window?.history.replaceState(null, '', newUrl);
        }
    }, [hasInitializedParams]);

    return upgradeInfo;
};

export default useRedirectFromUpgradePayment;
